// Ported from the product app's ai-elements. The `ai`-package tool types and
// the CodeBlock-backed ToolInput/ToolOutput (shiki) are dropped — the showcase
// only needs the collapsible header with a status icon and a body. lucide icon
// names are adapted to this project's lucide version.
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { CarrotIcon } from "@/components/CarrotIcon";
import { cn } from "@/lib/utils";
import { CircleCheck, CircleDashed, CircleX, LoaderCircle } from "lucide-react";
import type { ComponentProps, ReactNode } from "react";

export type ToolState =
  | "approval-requested"
  | "input-streaming"
  | "input-available"
  | "output-available"
  | "output-error";

export type ToolProps = ComponentProps<typeof Collapsible>;

export const Tool = ({ className, ...props }: ToolProps) => (
  <Collapsible className={cn("group/tool not-prose w-full", className)} {...props} />
);

/** Turn `search_horses` / `tool-getEvents` into "Search horses". */
export const formatToolName = (raw: string): string => {
  const words = raw
    .replace(/[_-]+/g, " ")
    .replace(/([a-z\d])([A-Z])/g, "$1 $2")
    .trim()
    .toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
};

const statusIcons: Record<ToolState, ReactNode> = {
  "approval-requested": <CircleDashed className="size-4 text-amber-600" />,
  "input-streaming": <LoaderCircle className="size-4 animate-spin text-primary" />,
  "input-available": <LoaderCircle className="size-4 animate-spin text-primary" />,
  "output-available": <CircleCheck className="size-4 text-emerald-600" />,
  "output-error": <CircleX className="size-4 text-destructive" />,
};

export type ToolHeaderProps = {
  title: string;
  state: ToolState;
  className?: string;
};

export const ToolHeader = ({ className, title, state }: ToolHeaderProps) => (
  <CollapsibleTrigger
    className={cn(
      "flex w-full items-center gap-2.5 rounded-md py-1 pr-2 text-left text-sm text-muted-foreground transition-colors hover:text-foreground",
      className,
    )}
  >
    <span className="shrink-0">{statusIcons[state]}</span>
    <span className="truncate font-medium">{title}</span>
    <CarrotIcon
      direction="right"
      className="ml-auto h-[1.125rem] w-3.5 shrink-0 opacity-55 transition-transform duration-200 ease-[cubic-bezier(0.23,1,0.32,1)] group-data-[state=open]/tool:rotate-0"
    />
  </CollapsibleTrigger>
);

export type ToolContentProps = ComponentProps<typeof CollapsibleContent>;

export const ToolContent = ({ className, ...props }: ToolContentProps) => (
  <CollapsibleContent
    className={cn(
      "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 ml-2 mt-1 space-y-2 border-l pl-3 text-popover-foreground outline-none data-[state=closed]:animate-out data-[state=open]:animate-in",
      className,
    )}
    {...props}
  />
);
